import { hasValue } from './equality';

/** Whether another location row could still get an active plant; a row with none picked takes one. */
export const hasPlantsLeft = (
  activePlantIds: string[],
  rowPlantIds: Array<string | null | undefined>,
): boolean => {
  const pickedIds = new Set(rowPlantIds.filter(hasValue));
  const unpickedRowCount = rowPlantIds.length - pickedIds.size;
  const freePlantCount = activePlantIds.filter((id) => !pickedIds.has(id)).length;

  return freePlantCount > unpickedRowCount;
};

/** Formik status of a form whose location rows get their first product row when the user picks a location. */
export type FirstProductRowStatus = { pendingFirstProductRows?: string[] };

// Keyed by the location list, not the row index, which shifts when an earlier location is removed.
// A location can be picked only once per list, so the key stays unique.
const rowKey = (productsPath: string, plantId: string): string =>
  `${productsPath.split('.')[0]}@${plantId}`;

const pendingRows = (status: FirstProductRowStatus | undefined): string[] =>
  status?.pendingFirstProductRows ?? [];

export const withFirstProductRowPending = (
  status: FirstProductRowStatus | undefined,
  productsPath: string,
  plantId: string,
): FirstProductRowStatus => ({
  ...status,
  pendingFirstProductRows: [...pendingRows(status), rowKey(productsPath, plantId)],
});

export const withoutFirstProductRowPending = (
  status: FirstProductRowStatus | undefined,
  productsPath: string,
  plantId: string,
): FirstProductRowStatus => ({
  ...status,
  pendingFirstProductRows: pendingRows(status).filter(
    (key) => key !== rowKey(productsPath, plantId),
  ),
});

export const isFirstProductRowPending = (
  status: FirstProductRowStatus | undefined,
  productsPath: string,
  plantId: string,
): boolean => pendingRows(status).includes(rowKey(productsPath, plantId));
